import { FC, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import DoorIcon from '@/components/icons/DoorIcon';
import { endOfYear, startOfYear } from 'date-fns';
import { useBookedApartmentsApi } from '@/features/calendar/hooks/useBookedApartmentsApi';
import { cn } from '@/lib/utils';

type Props = {
	selectedDate: Date;
	setApartmentId: (id: string) => void;
	containerClassName?: string;
};

const CalendarApartmentSelector: FC<Props> = ({
	selectedDate,
	setApartmentId,
	containerClassName,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedApartmentName, setSelectedApartmentName] = useState('');

	const { bookedApartmentsData } = useBookedApartmentsApi({
		startDate: startOfYear(selectedDate),
		endDate: endOfYear(selectedDate),
	});

	useEffect(() => {
		if (bookedApartmentsData?.items?.length && !selectedApartmentName) {
			const { id, name } = bookedApartmentsData.items[0];
			setSelectedApartmentName(name ?? '');
			setApartmentId(id);
		}
	}, [bookedApartmentsData, setApartmentId]);

	const isOpenToggler = () => {
		setIsOpen((prev) => !prev);
	};

	const handleApartmentSelect = (id: string, name: string) => {
		setApartmentId(id);
		setSelectedApartmentName(name);
	};

	return (
		<DropdownMenu onOpenChange={isOpenToggler}>
			<DropdownMenuTrigger
				asChild
				className={cn(
					'w-40 px-4 py-3 rounded-semi-xl',
					containerClassName,
				)}>
				<Button variant='outline'>
					<DoorIcon className='stroke-popover-1000' />
					<span className='w-fit truncate'>{selectedApartmentName}</span>
					<div
						className={`transition-transform duration-50 ease-in-out ${
							isOpen ? '-rotate-180' : 'rotate-0'
						}`}>
						{<ArrowDownIcon className='size-5' />}
					</div>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className={
					'w-(--radix-dropdown-menu-trigger-width) max-h-52 overflow-y-scroll no-scrollbar p-0 rounded-semi-xl border border-popover-100 bg-popover-10'
				}>
				{bookedApartmentsData?.items?.map(({ id, name }) => (
					<DropdownMenuCheckboxItem
						key={id}
						className='px-3.5 py-3'
						checked={selectedApartmentName === name}
						onCheckedChange={() =>
							handleApartmentSelect(id, name ?? '')
						}>
						{name}
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default CalendarApartmentSelector;
